// crewbit-admin/src/lib/t2125Mapping.ts
//
// Pure helpers for grouping expense rows by CRA T2125 line code on the client.
// Server-side `record_expense` writes `t2125_line` at insert via the SECDEF
// helper RPC `map_category_to_t2125`; this module reads `expense.t2125_line`
// off the row to build the section list. Client-side `EXPENSE_CATEGORY_GROUPS`
// supplies a human label per craLine.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses (server t2125 lookup
// is authoritative; client-side mapping is for display labels).
// Mirrors crewbit-mobile/lib/t2125Mapping.ts.

import { EXPENSE_CATEGORY_GROUPS } from '../constants/expenseCategories';

/**
 * Minimal shape this module reads off an expense row. Keeps the helpers
 * decoupled from the central `Expense` type, which doesn't yet expose
 * `t2125_line` (added server-side post-Session 99).
 */
export interface T2125ExpenseShape {
  category: string;
  amount: number;
  date: string;
  t2125_line?: string | null;
  ai_categorized?: boolean | null;
  ocr_confidence?: number | null;
}

export interface T2125Section<T extends T2125ExpenseShape = T2125ExpenseShape> {
  craLine: string;
  groupLabel: string;
  expenses: T[];
  subtotal: number;
}

const UNCATEGORIZED_LINE = 'uncategorized';

export function groupExpensesByT2125<T extends T2125ExpenseShape>(
  expenses: T[],
): T2125Section<T>[] {
  const grouped = new Map<string, T[]>();
  for (const e of expenses) {
    const line = e.t2125_line ?? UNCATEGORIZED_LINE;
    const bucket = grouped.get(line);
    if (bucket) bucket.push(e);
    else grouped.set(line, [e]);
  }

  return Array.from(grouped.entries())
    .map(([craLine, items]) => ({
      craLine,
      groupLabel: resolveGroupLabel(craLine),
      expenses: items.slice().sort((a, b) => b.date.localeCompare(a.date)),
      subtotal: items.reduce((sum, e) => sum + (e.amount ?? 0), 0),
    }))
    .sort((a, b) => a.craLine.localeCompare(b.craLine));
}

function resolveGroupLabel(craLine: string): string {
  if (craLine === UNCATEGORIZED_LINE) return 'Uncategorized';
  const group = EXPENSE_CATEGORY_GROUPS.find((g) =>
    g.subcategories.some((sub) => sub.craLine === craLine),
  );
  return group?.label ?? `Line ${craLine}`;
}

/**
 * Matches §Expenses.D3 "Needs review" definition: missing category, OR an
 * AI-categorized expense whose OCR confidence fell below the trust threshold.
 */
export function isNeedsReview(expense: T2125ExpenseShape): boolean {
  if (!expense.category || expense.category.trim() === '') return true;
  if (
    expense.ai_categorized === true &&
    expense.ocr_confidence !== null &&
    expense.ocr_confidence !== undefined &&
    expense.ocr_confidence < 0.7
  ) {
    return true;
  }
  return false;
}
